"use client";

import React from 'react';

type Action = { label: string; href: string };

/**
 * The one honest empty state. An empty section should say what it is, who
 * fills it, and where to go next — never vanish silently and never invent
 * placeholder content to look populated.
 *
 * Callers that pass no `action` keep the original two-link behavior.
 */
export default function EmptyState({
  title = 'Nothing here yet',
  subtitle = 'Try adjusting filters or explore popular content.',
  action,
  secondary,
  icon,
}: {
  title?: string;
  subtitle?: string;
  action?: Action;
  secondary?: Action;
  icon?: React.ReactNode;
}) {
  const fallbackLinks: Action[] = [
    { label: 'Browse search', href: '/search' },
    { label: 'See posts', href: '/posts' },
  ];

  return (
    <div className="rounded-2xl border border-border bg-card px-4 py-10 text-center">
      {icon && (
        <span className="ring-chip mx-auto mb-3 inline-grid h-10 w-10 place-items-center rounded-xl [&_svg]:h-5 [&_svg]:w-5">
          {icon}
        </span>
      )}
      <h2 className="text-lg font-semibold">{title}</h2>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{subtitle}</p>
      <div className="mt-4 flex flex-wrap items-center justify-center gap-3 text-sm">
        {action ? (
          <>
            <a
              href={action.href}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
            >
              {action.label} <span aria-hidden>→</span>
            </a>
            {secondary && (
              <a className="underline hover:no-underline" href={secondary.href}>
                {secondary.label}
              </a>
            )}
          </>
        ) : (
          fallbackLinks.map((l, i) => (
            <React.Fragment key={l.href}>
              {i > 0 && <span aria-hidden>•</span>}
              <a className="underline hover:no-underline" href={l.href}>
                {l.label}
              </a>
            </React.Fragment>
          ))
        )}
      </div>
    </div>
  );
}
